
import { generateObjectId } from '@builttocreate/engine-utils';

import { swapId } from './utils';
import FieldDisplayTypes from '../constants/FieldDisplayTypes';
import FieldTypes from '../constants/FieldTypes';
import FieldModes from '../constants/FieldModes';

/**
 * <--------------NOTICE----------------->
 * The functions mentioned below are used in the collection field as well
 * as the field validations. If you are making any changes to the functions
 * please make sure to check both the places this has been used.
 */

/**
 *
 * This helper method applies logic to determine if a child schema should be shown or hidden. Should
 * return true if the child is visible or false if child should be hidden.
 *
 * @param {Object} schemaEntry
 * @param {Object} rowCells
 * @param {String} fieldMode
 * @returns Boolean
 */
export const customApplyLogic = (schemaEntry, rowCells = {}, fieldMode) => {

  if (!schemaEntry) return false;

  // Always show in edit mode
  if (fieldMode === FieldModes.edit) return true;

  //Check if schema has logic. If schema has logic then continue,
  //or else return true if hidden is not set or false if hidden is set to true.
  if (!schemaEntry.logic) return typeof schemaEntry.hidden === 'boolean' ? !schemaEntry.hidden : true;

  const hasLogic = (schemaEntry.logic && schemaEntry.logic.action && schemaEntry.logic.eval && schemaEntry.logic.conditions && schemaEntry.logic.conditions.length > 0);

  if (!hasLogic) return typeof schemaEntry.hidden === 'boolean' ? !schemaEntry.hidden : true;

  const logic = schemaEntry.logic;

  const { action, eval: evalType, conditions } = logic;

  const evaluations = conditions.map(({ column, condition, value }) => {

    const cellValue = rowCells[column];

    switch (condition) {
      case '=': // equal
        return (
          (!Array.isArray(cellValue) && new RegExp(`^${value}$`, 'i').test(cellValue)) ||
          (Array.isArray(cellValue) && cellValue.includes(value))
        );

      case '!=': // notEqual
        return (
          (!Array.isArray(cellValue) && !(new RegExp(`^${value}$`, 'i').test(cellValue))) ||
          (Array.isArray(cellValue) && !cellValue.includes(value))
        );

      case '*=': // filled
        return (
          (cellValue && !Array.isArray(cellValue)) ||
          (Array.isArray(cellValue) && cellValue.length > 0)
        );

      case 'null=': // empty
        return (
          cellValue === '' ||
          cellValue === undefined ||
          cellValue === null ||
          (Array.isArray(cellValue) && cellValue.length < 1)
        );

      case '?=': // contain
        return (
          typeof cellValue === 'string' &&
          new RegExp(value, 'gi').test(cellValue)
        );

      case '>': // greaterThan
        return cellValue > value;

      case '<': // lessThan
        return cellValue < value;

      default:
        return false;
    }

  });

  const passed = evalType === 'and'
    ? evaluations.every(Boolean)
    : evaluations.some(Boolean);

  if (action === 'show') {
    return passed || (typeof schemaEntry.hidden === 'boolean' ? !schemaEntry.hidden : true);
  } else {
    return !passed && (typeof schemaEntry.hidden === 'boolean' ? !schemaEntry.hidden : true);
  }

};

export const flattenArray = (type, displayType, fieldPositionSchema, schema, value, disabled, fieldMode) => {

  const flattenedArray = [
    {
      type: 'fieldTitle',
      layoutType: 'title',
      layoutKey: 'title',
    }
  ];

  function traverseNodes(depth, node, parentId = null, schemaKey = null, nestedPath = '', rowIndex) {

    const isRoot = schema[schemaKey]?.root;

    if (!node || typeof node !== 'object') return;

    /**
     * Handle the value (rows) array
     */
    if (Array.isArray(node)) {

      const shouldChildrenDisplayHeader = node.length > 0 || isRoot;
      const shouldChildrenDisplayFooter = (node.length > 0 || isRoot) && !disabled;

      if (shouldChildrenDisplayHeader && displayType === FieldDisplayTypes.original) {
        flattenedArray.push(
          {
            _id: generateObjectId(),
            type: 'title',
            schemaKey,
            schema: schema[schemaKey],
            fieldPositionSchema: fieldPositionSchema?.[schemaKey] || {},
            parentId,
            depth,
            nestedPath,

            /**
             * layoutType and layoutKey are used to handle 
             * PDF Rendering, Page Breaks and Measurement 
             * management
             */
            layoutType: 'title', //Layout type should be header because header has special functioinality in PDF Rendering
            layoutKey: parentId ? `${parentId}-${schemaKey}-title` : `${schemaKey}-title`,
          },
        );

      }

      if (shouldChildrenDisplayHeader && type === FieldTypes.collection) {
        flattenedArray.push({
          _id: generateObjectId(),
          type: 'header',
          schemaKey,
          schema: schema[schemaKey],
          fieldPositionSchema: fieldPositionSchema?.[schemaKey] || {},
          parentId,
          depth,
          nestedPath,

          /**
           * layoutType and layoutKey are used to handle 
           * PDF Rendering page preaks and measurements.
           */
          layoutType: 'header',
          layoutKey: parentId ? `${parentId}-${schemaKey}-header` : `${schemaKey}-header`,
        });
      }

      // Update nestedPath for each item (using the index for uniqueness)
      node.forEach((item, index) => {
        const itemPath = nestedPath ? `${nestedPath}.${index}` : `${index}`;
        traverseNodes(depth, item, parentId, schemaKey, itemPath, index);
      });

      if (shouldChildrenDisplayFooter) {
        /**
         * Footer does not contain layoutType or layoutKey because
         * it is not rendering duering PDF rendering.
         */
        flattenedArray.push(
          {
            _id: generateObjectId(),
            type: 'footer',
            schemaKey,
            schema: schema[schemaKey],
            fieldPositionSchema: fieldPositionSchema?.[schemaKey] || {},
            parentId,
            depth,
            nestedPath
          },
        );
      }
    }

    /**
     * Handle the row obect and row children
     */
    else {

      const { _id, cells, children, tz } = node;
      // Create a current path for this row.
      const currentPath = nestedPath ? `${nestedPath}` : _id;

      const nestedValue = getNestedItem(value, currentPath);

      //We don't show the invalid indicator for PDF mode
      const isInvalid = detectEmptyChildren(fieldPositionSchema, schema, schemaKey, nestedValue, fieldMode);

      flattenedArray.push({
        _id,
        type: 'row',
        parentId,
        schemaKey,
        schema: schema[schemaKey],
        fieldPositionSchema: fieldPositionSchema?.[schemaKey] || {},
        cells: cells || {},
        rowIndex,
        depth,
        nestedPath: currentPath,
        isInvalid,
        tz,

        /**
         * layoutType and layoutKey are used to handle 
         * PDF Rendering page breaks and measurements.
         */
        header: parentId ? `${parentId}-${schemaKey}-header` : `${schemaKey}-header`,
        layoutType: 'row',
        layoutKey: parentId ? `${parentId}-${schemaKey}-${_id}` : `${schemaKey}-${_id}`,
      });

      if (children) {

        const defaultChildSchemaKeys = schema[schemaKey]?.children || [];
        const rowChildrenSchemaKeys = children ? Object.keys(children) : [];
        const mergedSchemaKeys = defaultChildSchemaKeys.concat(rowChildrenSchemaKeys);
        const nestedSchemaKeys = mergedSchemaKeys.filter((item, index) => mergedSchemaKeys.indexOf(item) === index); //Remove duplicates

        nestedSchemaKeys.forEach((childSchemaKey) => {

          const childSchema = schema[childSchemaKey];
          const childData = children?.[childSchemaKey];
          const childPath = `${currentPath}.children.${childSchemaKey}.value`; // Append the child schema key to the current path
          const isVisible = customApplyLogic(childSchema, cells, fieldMode);

          if (childData && isVisible) {
            traverseNodes(depth + 1, childData.value, _id, childSchemaKey, childPath, undefined, cells);
          }
        });
      }
    }
  }

  const rootSchemaKey = Object.keys(schema).find((key) => schema[key].root);
  traverseNodes(0, value, null, rootSchemaKey);

  return flattenedArray;

};

/**
 * Retruns the value corresponding to the nested path.
 *
 * @param {array} data
 * @param {string} path
 * @returns{object}
 */
export const getNestedItem = (data, path) => {

  // Normalize the path into an array; convert numeric strings to numbers.
  const keys = Array.isArray(path)
    ? path
    : path.split('.').map((k) => (isNaN(k) ? k : Number(k)));

  // If path is "0" or not provided, we return the data itself.
  if (!path) {
    return data;
  }

  // Traverse the data structure following the keys.
  return keys.reduce((acc, key) => {
    if (acc === undefined || acc === null) {
      return undefined;
    }
    return acc[key];
  }, data);

};

export const detectEmptyChildren = (fieldPositionSchema, schemaObj, schemaKey, nestedValue, fieldMode) => {

  let emptyChildren = false;

  const rootSchema = schemaObj[schemaKey];

  if (fieldMode === FieldModes.readonly) return;

  //Logic that checks for nested schema validation
  rootSchema.children.forEach((childSchemaId) => {

    const childSchema = schemaObj[childSchemaId];

    const isChildVisible = customApplyLogic(childSchema, nestedValue.cells);

    if (childSchema.required && isChildVisible) {

      if (!nestedValue.children[childSchemaId] || !nestedValue.children[childSchemaId].value || nestedValue.children[childSchemaId].value.length === 0) {
        emptyChildren = true;
      }

    }

  });

  //Logic that checks validaton for required columns. Ignores any columns that are hidden.
  const targetFieldPositionSchema = fieldPositionSchema[schemaKey] || { tableColumns: {} };
  const requiredColumns = rootSchema.tableColumns.filter((column) => column.required && !targetFieldPositionSchema?.tableColumns?.[column._id]?.hidden);

  if (requiredColumns) {

    requiredColumns.forEach((column) => {
      if (!column.hidden && !nestedValue.cells[column._id]) {
        emptyChildren = true;
      }

    });

  }

  return emptyChildren;

};

/**
 * This function updates the schema and any children references to the schemawith readable 
 * IDs based on the provided readableIdMap lookup.
 * 
 * @param {*} schema 
 * @param {*} readableIdMap { oldCollectionId: newReadableId}
 * @returns {Object} - The updated schema and children references with new readable IDs.
 */
export const updateSchemaWithReadableIds = (schema, readableIdMap) =>  {

  const updatedSchema = {};

  // Step 1: Rename top-level keys
  for (const oldKey in schema) {

    const newKey = readableIdMap[oldKey] || oldKey;
    const individualSchema = { ...schema[oldKey] };

    // Step 2: Replace child IDs if needed
    if (Array.isArray(individualSchema.children)) {
      individualSchema.children = individualSchema.children.map(childId =>
        readableIdMap[childId] || childId
      );
    }

    // Step 2: Replace logic schema IDs if needed
    if (individualSchema.logic && individualSchema.logic.conditions) {
      individualSchema.logic.conditions = individualSchema.logic.conditions.map(condition => {
        return { ...condition, schema: readableIdMap[condition.schema] || condition.schema };
      });
    }

    updatedSchema[newKey] = individualSchema;

  }

  return updatedSchema;

}

/**
 * Updates the field value data structure by replacing schema keys with their corresponding readable IDs.
 * 
 * This function is used when a schema's _id is changed to a new readable ID, and all references to the old schemaKey
 * in the nested field value structure (including children) need to be updated to the new key.
 * 
 * @param {Array|Object} fieldValue - The original field value (rows) to update.
 * @param {Array} flattenedRows - An array of objects representing the flattened structure of the field value,
 *                                each containing a `nestedPath` (dot-separated path to the schemaKey) and `schemaKey`.
 * @param {Object} readableIdMap - A mapping of old schema keys to new readable IDs (e.g., { oldId: newId }).
 * @returns {Array|Object} - The updated field value with schema keys replaced by their new readable IDs.
 */
export function updateFieldValueWithReadableIds(fieldValue, flattenedRows, readableIdMap) {
  // Helper function to deep clone the fieldValue to avoid mutating the original data
  const cloneDeep = (obj) => JSON.parse(JSON.stringify(obj));

  // Create a deep copy of the fieldValue to work on
  const updatedRows = cloneDeep(fieldValue);

  // Iterate over each flattened row, which contains the path and schemaKey to potentially update
  for (const row of flattenedRows) {
    const { nestedPath, schemaKey } = row;
    // Find the new key for this schemaKey, if it exists in the readableIdMap
    const newKey = readableIdMap[schemaKey];
    // If there is no new key or the key hasn't changed, skip this row
    if (!newKey || newKey === schemaKey) continue;

    // Split the nestedPath into its parts for traversal
    const parts = nestedPath.split('.');

    // Start traversing from the root of the updatedRows structure
    let ref = updatedRows;

    // Traverse the path up to the parent of the key to be renamed
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      const nextKey = parts[i + 1];

      // If the current reference is an array, use numeric index
      if (Array.isArray(ref)) {
        ref = ref[Number(key)];
      } else {
        ref = ref[key];
      }

      // If the reference is undefined or null, break out of the loop
      if (!ref) break;

      // Check if we're at the parent object whose child key needs to be renamed
      // Specifically, if the current key is "children" and the next key is the schemaKey to rename
      if (
        key === "children" &&
        nextKey === schemaKey &&
        typeof ref === "object" &&
        ref.hasOwnProperty(schemaKey)
      ) {
        // Rename the key in place: assign the value to the new key and delete the old key
        ref[newKey] = ref[schemaKey];
        delete ref[schemaKey];
        // Once renamed, break out of the traversal for this row
        break;
      }
    }
  }

  // Return the updated field value structure with schema keys replaced
  return updatedRows;
}

/**
 * For Document fieldPositions that have multiple table cell fieldPositions associated
 * with the same table field, we need to keep the column property in sync.
 * 
 * @param {*} nextDoc 
 * @param {*} fileId 
 * @param {*} fieldId 
 * @param {*} columnChanges //Column change data: { schemaId, oldColumnId, newColumnId }
 */
export const syncCollectionFieldColumnIdChange = (nextDoc, fileId, fieldId, fieldIndex, columnChanges) => {

  const { schemaId, oldColumnId, newColumnId } = columnChanges;

  const fileIndex = nextDoc.files.findIndex(f => f._id === fileId);

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * Step 1: Handle fieldPosition updates 
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */
  const updateFieldPositionColumnId = (fp) => {
    if (fp.field !== fieldId) return fp;

    const nextFp = { ...fp };
   
    //Update the tableColumns property to the newColumnId if it matches the oldColumnId
    if (nextFp.schema && nextFp.schema?.[schemaId] && nextFp.schema?.[schemaId]?.tableColumns && nextFp.schema?.[schemaId]?.tableColumns[oldColumnId]) {
      nextFp.schema[schemaId].tableColumns = swapId(nextFp.schema?.[schemaId]?.tableColumns, oldColumnId, newColumnId);
    }

    return nextFp;

  }
    
  // Update primary view fieldPositions
  nextDoc.files[fileIndex].pages = nextDoc.files[fileIndex].pages.map(page => { 
    page.fieldPositions = page.fieldPositions.map(updateFieldPositionColumnId); 
    return page; 
  });
  
  // Update mobile view fieldPositions if they exist
  if (nextDoc.files[fileIndex].views && nextDoc.files[fileIndex].views.length > 0) {
    nextDoc.files[fileIndex].views = nextDoc.files[fileIndex].views.map(view => {
      if (view.pages) {
        view.pages = view.pages.map(page => {
          page.fieldPositions = page?.fieldPositions.map(updateFieldPositionColumnId);
          return page;
        });
      }
      return view;
    });
  }

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * Step 1: Handle field updates 
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  //Handle updating the schema conditional logic references to the newColumnId if it matches the oldColumnId
  nextDoc.fields[fieldIndex].schema = swapColumnIdInLogicConditions(nextDoc.fields[fieldIndex].schema, schemaId, oldColumnId, newColumnId);

  return nextDoc;

}

/**
 * Swaps all occurrences of a column id with a new column id in the logic.conditions array
 * for all schema objects in the given schemaObj.
 *
 * @param {Object} schemaObj - The schema object, keyed by schemaId.
 * @param {string} oldColumnId - The column id to replace.
 * @param {string} newColumnId - The new column id to use.
 * @returns {Object} - A new schema object with column ids swapped in logic.conditions.
 */
export function swapColumnIdInLogicConditions(schemaObj, targetSchemaId, oldColumnId, newColumnId) {
  if (!schemaObj || typeof schemaObj !== 'object') return schemaObj;

  // Deep clone to avoid mutation
  const nextSchemaObj = JSON.parse(JSON.stringify(schemaObj));

  Object.keys(nextSchemaObj).forEach((schemaId) => {
    const schema = nextSchemaObj[schemaId];
    if (
      schema &&
      schema.logic &&
      Array.isArray(schema.logic.conditions)
    ) {
      schema.logic.conditions = schema.logic.conditions.map((condition) => {

        //IMPORTANT NOTE: Only update the condition if the schemaId matches the targetSchemaId 
        //and the columnId matches the oldColumnId. Different schemas could have the same columnId,
        //this ensures only the condition for the targetSchemaId is updated.
        if (condition && condition.schema === targetSchemaId && condition.column === oldColumnId) {
          return { ...condition, column: newColumnId };
        }
        return condition;
      });
    }
  });

  return nextSchemaObj;
}
